import { useState } from "react";
import { Box, Radio } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import {
  Copy,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Image,
  Lock,
  LockOpen,
  Trash2,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { LayerNameEditor } from "../../../components/Modal/ModalImageEdit/shared";
import { getDisplaySiblingIDs } from "../instance/instanceGraph";
import { normalizeChildOrder } from "../utils/layerTree";
import {
  layerListNoNativeSelectSx,
  preventNativeTextSelectOnAdditivePointer,
} from "./canvasLayerSelection";
import {
  layerListAutoScroll,
  sortableLayerRowStyle,
} from "./layerListDnd";
import LayerTryScanOverlay from "./layerTryScan";

const SELECTED_BORDER = "#111";
const NESTED_LAYER_INDENT = 2.5;
const CHEVRON_SLOT_WIDTH = 16;

const rowIconButtonSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(0,0,0,0.45)",
  cursor: "pointer",
  flexShrink: 0,
  border: 0,
  bgcolor: "transparent",
  p: 0.25,
  "&:hover": { color: "rgba(0,0,0,0.8)" },
};

const SortableLayerRow = ({
  item,
  selected,
  hasChildren,
  expanded,
  isGenerating = false,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onDelete,
  onCopy,
  onToggleLock,
  onToggleVisible,
  fallbackName,
  deleteLabel,
  copyLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: Boolean(disabled) });
  const name = item.node.name || fallbackName;
  const ownVisible = item.parentID
    ? item.node.layout?.visible !== false
    : item.box?.visible !== false;
  const isHidden = item.box?.visible === false;

  return (
    <Box
      ref={setNodeRef}
      style={sortableLayerRowStyle({ transform, transition, isDragging })}
      onMouseDown={preventNativeTextSelectOnAdditivePointer}
      onClick={(event) => onSelect(item.id, event)}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        pr: 1,
        py: 0.875,
        border: selected
          ? `1.5px solid ${SELECTED_BORDER}`
          : "1px solid rgba(0,0,0,0.08)",
        borderRadius: 2,
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "left",
        opacity: isDragging ? 0.7 : isHidden ? 0.45 : 1,
        boxShadow: isDragging ? "0 8px 20px rgba(17, 24, 39, 0.12)" : "none",
        zIndex: isDragging ? 2 : 1,
        position: "relative",
        overflow: isGenerating ? "hidden" : "visible",
        ...layerListNoNativeSelectSx,
      }}
    >
      {isGenerating ? <LayerTryScanOverlay /> : null}
      {hasChildren ? (
        <Box
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand(item.id);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: CHEVRON_SLOT_WIDTH,
            color: "rgba(0,0,0,0.4)",
            flexShrink: 0,
          }}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </Box>
      ) : null}
      <Box
        component="span"
        ref={disabled ? undefined : setActivatorNodeRef}
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        onClick={(event) => event.stopPropagation()}
        onDragStart={(event) => event.preventDefault()}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(0,0,0,0.28)",
          cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitUserDrag: "none",
          flexShrink: 0,
        }}
      >
        <DragIndicator fontSize="small" sx={{ pointerEvents: "none" }} />
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.100",
          flexShrink: 0,
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {item.url ? (
          <Box
            component="img"
            src={item.url}
            alt={name}
            draggable={false}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              pointerEvents: "none",
              WebkitUserDrag: "none",
            }}
          />
        ) : item.node.type === "shape" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: item.node.shape?.fill || "#46248C",
            }}
          />
        ) : item.node.type === "group" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            G
          </Box>
        ) : item.node.type === "image" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#111",
            }}
          >
            <Image size={16} strokeWidth={2} />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.25,
              color: "#111",
              fontSize: 9,
              fontWeight: 700,
              overflow: "hidden",
            }}
          >
            Aa
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {disabled ? (
          <Box
            sx={{
              fontWeight: 700,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.3,
              fontSize: "0.875rem",
            }}
          >
            {name}
          </Box>
        ) : (
          <LayerNameEditor
            name={name}
            onStartEdit={() => onSelect(item.id)}
            onCommit={(nextName) => {
              const trimmed = String(nextName || "").trim();
              if (trimmed === name) return;
              onRename(item.id, trimmed);
            }}
          />
        )}
      </Box>
      {disabled ? null : (
        <>
          {item.isBackground || !onCopy ? null : (
            <Box
              component="button"
              type="button"
              title={copyLabel}
              aria-label={copyLabel}
              onClick={(event) => {
                event.stopPropagation();
                onCopy(item.id);
              }}
              sx={rowIconButtonSx}
            >
              <Copy size={15} />
            </Box>
          )}
          {onToggleVisible ? (
            <Box
              component="button"
              type="button"
              title={ownVisible ? hideLabel : showLabel}
              aria-label={ownVisible ? hideLabel : showLabel}
              onClick={(event) => {
                event.stopPropagation();
                onToggleVisible(item.id, !ownVisible);
              }}
              sx={{
                ...rowIconButtonSx,
                color: ownVisible ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.8)",
              }}
            >
              {ownVisible ? <Eye size={15} /> : <EyeOff size={15} />}
            </Box>
          ) : null}
          {onToggleLock ? (
            <Box
              component="button"
              type="button"
              title={item.node.locked ? unlockLabel : lockLabel}
              aria-label={item.node.locked ? unlockLabel : lockLabel}
              onClick={(event) => {
                event.stopPropagation();
                onToggleLock(item.id, !item.node.locked);
              }}
              sx={{
                ...rowIconButtonSx,
                color: item.node.locked
                  ? "rgba(0,0,0,0.8)"
                  : "rgba(0,0,0,0.45)",
              }}
            >
              {item.node.locked ? <Lock size={15} /> : <LockOpen size={15} />}
            </Box>
          ) : null}
          <Box
            component="button"
            type="button"
            title={deleteLabel}
            aria-label={deleteLabel}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(item.id);
            }}
            sx={rowIconButtonSx}
          >
            <Trash2 size={15} />
          </Box>
        </>
      )}
      <Radio
        checked={selected}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onSelect(item.id, event)}
        size="small"
        disabled={Boolean(disabled)}
        sx={{
          p: 0.5,
          color: "rgba(0,0,0,0.22)",
          "&.Mui-checked": { color: SELECTED_BORDER },
        }}
      />
    </Box>
  );
};

const SiblingList = ({
  parentID,
  nodes,
  nodeLayouts,
  itemsById,
  tryingNodeIDs = {},
  selectedID,
  selectedIDs = [],
  expandedIDs,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onReorder,
  onDelete,
  onCopy,
  onToggleLock,
  onToggleVisible,
  fallbackName,
  deleteLabel,
  copyLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
}) => {
  const siblingIds = getDisplaySiblingIDs({
    nodes,
    nodeLayouts,
    parentID,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [activeId, setActiveId] = useState(null);

  const onDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const onDragEnd = (event) => {
    setActiveId(null);
    if (disabled) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = siblingIds.indexOf(active.id);
    const newIndex = siblingIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder({
      parentID,
      orderedDisplayIds: arrayMove(siblingIds, oldIndex, newIndex),
    });
  };

  const activeItem = activeId ? itemsById.get(activeId) : null;
  const activeIndex = activeId ? siblingIds.indexOf(activeId) : -1;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll={layerListAutoScroll}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={siblingIds}
        strategy={verticalListSortingStrategy}
      >
        {siblingIds.map((id, index) => {
          const item = itemsById.get(id);
          if (!item) return null;
          const hasChildren = item.node.type === "group";
          const expanded = Boolean(expandedIDs[id]);
          const childIDs = hasChildren
            ? normalizeChildOrder({
                childOrder: item.node.childOrder,
                children: item.node.children,
              })
            : [];
          return (
            <Box
              key={id}
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              <SortableLayerRow
                item={item}
                selected={
                  selectedIDs.length
                    ? selectedIDs.includes(id)
                    : selectedID === id
                }
                hasChildren={hasChildren && childIDs.length > 0}
                expanded={expanded}
                isGenerating={Boolean(tryingNodeIDs[id])}
                disabled={disabled}
                onSelect={onSelect}
                onRename={onRename}
                onToggleExpand={onToggleExpand}
                onDelete={onDelete}
                onCopy={onCopy}
                onToggleLock={onToggleLock}
                onToggleVisible={onToggleVisible}
                fallbackName={fallbackName.replace(
                  "{{index}}",
                  String(index + 1)
                )}
                deleteLabel={deleteLabel}
                copyLabel={copyLabel}
                lockLabel={lockLabel}
                unlockLabel={unlockLabel}
                hideLabel={hideLabel}
                showLabel={showLabel}
              />
              {hasChildren && expanded ? (
                <Box
                  sx={{
                    pl: NESTED_LAYER_INDENT,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <SiblingList
                    parentID={id}
                    nodes={nodes}
                    nodeLayouts={nodeLayouts}
                    itemsById={itemsById}
                    tryingNodeIDs={tryingNodeIDs}
                    selectedID={selectedID}
                    selectedIDs={selectedIDs}
                    expandedIDs={expandedIDs}
                    disabled={disabled}
                    onSelect={onSelect}
                    onRename={onRename}
                    onToggleExpand={onToggleExpand}
                    onReorder={onReorder}
                    onDelete={onDelete}
                    onCopy={onCopy}
                    onToggleLock={onToggleLock}
                    onToggleVisible={onToggleVisible}
                    fallbackName={fallbackName}
                    deleteLabel={deleteLabel}
                    copyLabel={copyLabel}
                    lockLabel={lockLabel}
                    unlockLabel={unlockLabel}
                    hideLabel={hideLabel}
                    showLabel={showLabel}
                  />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </SortableContext>
      <DragOverlay zIndex={2000} style={{ cursor: "grabbing" }}>
        {activeItem ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 1,
              pr: 1,
              py: 0.875,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 2,
              bgcolor: "#fff",
              boxShadow: "0 8px 20px rgba(17, 24, 39, 0.16)",
              pointerEvents: "none",
            }}
          >
            <DragIndicator fontSize="small" sx={{ color: "rgba(0,0,0,0.28)" }} />
            <Box sx={{ minWidth: 0, flex: 1, fontWeight: 700, fontSize: 14 }}>
              {activeItem.node.name ||
                fallbackName.replace(
                  "{{index}}",
                  String(Math.max(activeIndex, 0) + 1)
                )}
            </Box>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

const SortableNestedLayerList = ({
  nodes = {},
  nodeLayouts = {},
  itemsById,
  tryingNodeIDs = {},
  selectedID,
  selectedIDs = [],
  expandedIDs,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onReorder,
  onDelete,
  onCopy,
  onToggleLock,
  onToggleVisible,
  fallbackName,
  deleteLabel,
  copyLabel,
  lockLabel,
  unlockLabel,
  hideLabel,
  showLabel,
}) => (
  <SiblingList
    parentID={null}
    nodes={nodes}
    nodeLayouts={nodeLayouts}
    itemsById={itemsById}
    tryingNodeIDs={tryingNodeIDs}
    selectedID={selectedID}
    selectedIDs={selectedIDs}
    expandedIDs={expandedIDs}
    disabled={disabled}
    onSelect={onSelect}
    onRename={onRename}
    onToggleExpand={onToggleExpand}
    onReorder={onReorder}
    onDelete={onDelete}
    onCopy={onCopy}
    onToggleLock={onToggleLock}
    onToggleVisible={onToggleVisible}
    fallbackName={fallbackName}
    deleteLabel={deleteLabel}
    copyLabel={copyLabel}
    lockLabel={lockLabel}
    unlockLabel={unlockLabel}
    hideLabel={hideLabel}
    showLabel={showLabel}
  />
);

export default SortableNestedLayerList;
